/**
 * Nav Visibility Configuration — Phase 30 Wave 1 (30.1.3)
 *
 * THIS FILE IS A REGISTRY. It declares the static defaults + visibility shape
 * per route. The actual runtime visibility is composed by the dispatcher in
 * `apps/web/src/layouts/DashboardLayout.tsx`, which reads BOTH this file's
 * `defaultVisible` AND the corresponding `import.meta.env.VITE_HIDE_NAV_*`
 * env override to produce the final boolean.
 *
 * RULE 2 PARAMETRIC PATTERN:
 *   - Declaration: this file (defaultVisible + locked, static data only)
 *   - Dispatch: `isNavItemVisible(routeKey)` and `isNavItemLocked(routeKey)`
 *     in DashboardLayout.tsx — see the
 *     `// PARAMETRIC-DISPATCH: NAV_VISIBILITY_CONFIG[<key>].defaultVisible + VITE_HIDE_NAV_<KEY>`
 *     marker at the env-read site.
 *   - Verification: marker comment + manual env-var flip test (Chunk 1).
 *
 * KEEP THIS FILE FREE OF `process.env` / `import.meta.env` READS.
 * Env overrides happen at the dispatch site (Vite-only). Pure-data here
 * means safe import from any runtime.
 *
 * Adding a new flag:
 *   1. Add an entry to NAV_VISIBILITY_CONFIG below.
 *   2. Tag the matching <NavItem> with `routeKey: '<key>'` in
 *      DashboardLayout.tsx (TypeScript will require the literal to match
 *      a key of NAV_VISIBILITY_CONFIG — typo-safe).
 *   3. (Prod-hide only) Add a VITE_HIDE_NAV_<KEY> env var read at the
 *      dispatch site (deterministic name: dots and dashes both map to `_`,
 *      letters uppercase. Example: `analytics.skill-matrix` →
 *      `VITE_HIDE_NAV_ANALYTICS_SKILL_MATRIX`).
 *
 * Hidden routes — render or 403?
 *   When a flag hides a nav item, the underlying URL still renders if a
 *   user types it directly. The flag's intent is "don't surface this in
 *   nav," not "ban access." Customers with bookmarks keep them working.
 *   If a future requirement is "hard hide," that's a separate ticket and
 *   a different flag (`VITE_BLOCK_NAV_*` vs. `VITE_HIDE_NAV_*`).
 */

export interface NavVisibilityEntry {
  /**
   * Static default visibility. The dispatcher in DashboardLayout.tsx
   * composes this with VITE_HIDE_NAV_<KEY> env override (env=true wins → hide).
   * Hardcoding `true` here without the env-aware dispatcher is meaningless;
   * the env read at the dispatcher is the actual runtime branch (Rule 2).
   */
  defaultVisible: boolean;

  /**
   * When true, the nav item renders with a lock icon and the underlying
   * route serves the LockedFeaturePage upgrade prompt. The dispatcher reads
   * this flag and branches at the consumer site (lock badge JSX) — genuinely
   * parametric. Independent of `defaultVisible` — a locked item is still
   * visible by default.
   */
  locked?: boolean;
}

export const NAV_VISIBILITY_CONFIG = {
  // Phase 30.1.3 — prod-hide for in-flight admin/manager-only surfaces.
  // defaultVisible:true → staging shows everything; production .env sets
  // VITE_HIDE_NAV_ANALYTICS_RING_2_EFFECTIVENESS=true at build time.
  'analytics.ring-2-effectiveness': { defaultVisible: true },
  'analytics.skill-matrix': { defaultVisible: true },

  // Phase 30.1.2 — locked "upgrade" placeholders for real future surfaces.
  // defaultVisible:true so the nav slot renders; locked:true triggers the
  // lock icon + upgrade-CTA page (genuinely parametric — dispatcher branches).
  // (Tagged on the matching nav items in Chunk 2.)
  'dashboard.command-center': { defaultVisible: true, locked: true },
  'library.machine-fleet': { defaultVisible: true, locked: true },
} as const satisfies Record<string, NavVisibilityEntry>;

export type NavRouteKey = keyof typeof NAV_VISIBILITY_CONFIG;
